<template>
  <div>
    <b-row>
      <b-col cols="12" order="2" lg="8" order-lg="1">

        <table id="template-builder" align="center" role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" :style="'margin: auto; background-color: #ffffff;'" class="email-container">

          <template-header
            :organization="organization"
            :show_brand_header="show_brand_header"
            :show_full_logo="use_full_logo"
            :background-color="content_background_color || organization.color || '#ffffff'"
            :disabled="busy || isLocked"
          ></template-header>

          <draggable v-model="blocks" group="blocks" @start="drag=true" @end="drag=false" @change="onOrderChange" :disabled="busy || isLocked">
            <block-edit
              v-for="(block, index) in blocks"
              v-bind:key="block.id"
              :organization="organization"
              :template="template"
              :recipient="recipient"
              :block="block"
              :placeholders="placeholdersForBlocks"
              :button_color="button_color"
              :button_radius="button_radius"
              :content_background_color="content_background_color"
              @change-block="blocks.splice(index, 1, $event); $emit('saved')"
              @delete-block="blocks.splice(index, 1); $emit('saved')"
              @add-block-before="insert_at = index; $refs.newblock.showModal = true;"
              @add-block-after="insert_at = index+1; $refs.newblock.showModal = true;"
              @duplicate="blocks.splice(index, 0, $event); $emit('saved')"
              :disabled="busy || isLocked"
            />
          </draggable>

          <tr>
            <block-edit
              ref="newblock"
              :organization="organization"
              :template="template"
              :recipient="recipient"
              :block="{}"
              :button_color="button_color"
              :button_radius="button_radius"
              :content_background_color="content_background_color"
              @change-block="blocks.splice(insert_at !== null ? insert_at : blocks.length, 0, $event); onOrderChange()"
              :disabled="busy || isLocked"
            />
          </tr>
        </table>
        
        <table id="template-builder-add" align="center" role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" :style="`margin: auto; background-color: ${content_background_color};`" class="email-container">
          <tr>
            <td style="text-align: center;">
              <b-button
                variant="outline-primary"
                size="lg"
                @click="insert_at = null; $refs.newblock.showModal = true;"
                :title="trans('new_block')"
                class="my-2"
                :disabled="busy || isLocked"
              >
                <i class="fa fa-fw fa-plus"></i>
              </b-button>
            </td>
          </tr>
        </table>

        <template-footer :organization="organization" :show_actions="true" :background_color="content_background_color"></template-footer>

      </b-col>
      <b-col cols="12" order="1" lg="4" order-lg="2" class="mb-2">
        <b-card style="max-width: 600px; margin: auto;">
          <b-card-text>
            <b-form-checkbox v-model="show_brand_header" :value="true" :unchecked-value="false" @input="onShowBrandHeaderChange" :disabled="busy || isLocked">
              {{ trans('show_brand_header') }}
            </b-form-checkbox>
            <b-form-group v-if="!!show_brand_header && !!organization.full_logo" class="mt-2 ml-2">
              <b-form-checkbox switch size="sm" v-model="use_full_logo" :disabled="busy" @change="onUseFullLogoChange">
                {{ trans('use_full_logo') }}
              </b-form-checkbox>
            </b-form-group>
          </b-card-text>

          <b-card-text v-if="organization.can_publish" class="mt-2">
            <b-form-group>
              <b-form-checkbox v-model="published_to_partner" :value="organization.partner" :unchecked-value="null" @input="onPublishToPartnerChange" :disabled="busy || isLocked">
                <label>
                  {{ trans('published_to_partner').replace(':partner', organization.partner) }}
                  <small class="text-muted" :title="trans('published_to_partner_about')" v-b-tooltip.hover>
                    <i class="fa fa-fw fa-info-circle"></i>
                  </small>
                </label>
              </b-form-checkbox>
            </b-form-group>
          </b-card-text>

          <b-card-text v-if="blocksHasPlaceholders > 0 && can_choose_list">
            <hr/>
            <b-form-checkbox v-model="test_placeholders" :value="true" :unchecked-value="false" :disabled="busy">
              {{ trans('test_placeholders') }}
            </b-form-checkbox>
            <b-form-group class="mt-2" v-if="test_placeholders">
              <list-dropdown
                :organization="organization"
                v-model="list_id"
                :disabled="busy"
              />
            </b-form-group>
            <list-recipients v-if="test_placeholders && list_id"
              :organization="organization"
              :list_id="list_id"
              @recipient="recipient = $event"
              @placeholders="placeholdersForBlocks = $event"
            ></list-recipients>
          </b-card-text>

          <b-card-text>
            <hr/>
            <h6>
              <b-button variant="link" class="pl-0" v-b-toggle.show-colors>
                <i :class="`fa fa-fw fa-${show_colors ? 'minus-square' : 'plus-square'}`"></i>
                <span class="text-dark">
                  {{ trans('color_options') }}
                </span>
              </b-button>
            </h6>
            <b-collapse id="show-colors" v-model="show_colors">
              <b-row>
                <b-col>
                  <b-form-group :label="trans('button')">
                    <b-form-input id="button_color" v-model="button_color" @input="onButtonColorChange" :disabled="busy || isLocked" />
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group :label="trans('background')">
                    <b-form-input id="content_background_color" v-model="content_background_color" @input="onContentBackgroundColorChange" :disabled="busy || isLocked" />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-collapse>
          </b-card-text>

          <b-card-text v-if="organization.id == 9">
            <hr/>
            <h6>{{ trans('page') }}</h6>
            <b-form-group>
              <page-dropdown
                :organization="organization"
                v-model="page_id"
                nullable
                :disabled="busy"
                @input="updatePage"
              ></page-dropdown>
            </b-form-group>
          </b-card-text>
        </b-card>
        <b-alert v-if="!!template.type" variant="primary" show style="max-width: 600px; margin: 1em auto;">
          <p>
            <strong>
              <i class="fa fa-fw fa-info-circle"></i>
              {{ trans('default_template') }}
            </strong>
          </p>
          <p>
            {{ trans('default_template_about').replace(':type', template.type.replace('_', ' ')) }}
          </p>
          <p>
            <b-button variant="outline-primary" size="sm" @click="onClickResetDefault" :disabled="busy || isLocked">
              {{ trans('reset') }}
            </b-button>
          </p>
        </b-alert>
      </b-col>
    </b-row>

  </div>
</template>

<style>
  .form-group .sp-replacer {
    width: 100%;
    border-radius: 1.25rem;
    padding: 7px;
    border-color: #929FBA;
  }
  .form-group .sp-replacer .sp-preview {
    width: 80%;
    height: 24px;
    border-top-left-radius: 1.25rem;
    border-bottom-left-radius: 1.25rem;
    overflow: hidden;
  }
  .form-group.form-group-sm .sp-replacer .sp-preview {
    height: 18px;
  }
</style>

<script>
  import TemplateHeader from './TemplateHeaderComponent'
  import TemplateFooter from './TemplateFooterComponent'
  import ListRecipients from './../list/ListRecipients'
  import ListDropdown from './../list/ListDropdown'
  import PageDropdown from './../page/PageDropdown'
  import Datetime from 'vue-bootstrap-datetimepicker'
  import BlockEdit from './BlockEditComponent'
  import draggable from 'vuedraggable'
  import 'spectrum-colorpicker/spectrum.js'

  export default {
    props: {
      organization: Object,
      template: Object,
      can_choose_list: {
        type: Boolean,
        default: true,
      },
      show_test_placeholders: Boolean,
      initial_list_id: Number,
    },
    components: {
      TemplateHeader,
      TemplateFooter,
      ListRecipients,
      ListDropdown,
      PageDropdown,
      Datetime,
      BlockEdit,
      draggable
    },
    data() {
      return {
        trans: trans,
        moment: moment,
        busy: false,
        error: null,
        showAdminTools: window.SHOW_ADMIN_TOOLS,
        ajaxUrl: '/organization/' + this.organization.id + '/template/' + this.template.id + '/blocks',
        blocks: null,
        show_brand_header: this.template.show_brand_header,
        background_color: this.template.background_color,
        background_image: this.template.background_image,
        page_id: this.template.page_id,
        dateOptions: {
          locale: window.lang,
          format: 'LLL',
          showClear: true,
        },
        show_colors: false,
        button_color: this.template.button_color || this.organization.button_color,
        button_radius: this.organization.button_radius,
        content_background_color: this.template.content_background_color || '#ffffff',
        test_placeholders: this.show_test_placeholders,
        list_id: this.initial_list_id || null,
        recipient: null,
        published_to_partner: this.template.published_to_partner,
        insert_at: null,
        placeholdersForBlocks: null,
        use_full_logo: this.template.use_full_logo || false,
      }
    },
    watch: {
      show_test_placeholders() {
        this.test_placeholders = this.show_test_placeholders
      },
      test_placeholders() {
        if (!this.test_placeholders) this.placeholdersForBlocks = null
      },
      initial_list_id() {
        this.list_id = this.initial_list_id || null
      },
    },
    computed: {
      isLocked() {
        return !!this.template && !!this.template.is_locked_by_user_id
      },
      blocksHasPlaceholders() {
        return _.reduce(this.blocks || [], (has, block) => {
          const matches = (block.text||'').match(/{(.*?)}/g)
          if (matches && matches.length || block.type == 'paper') has = true
          return has
        }, false)
      },
    },
    methods: {
      onButtonColorChange(color) {
        this.button_color = color ? color.toHexString() : this.organization.button_color
        //console.log('onButtonColorChange', color, this.button_color)
        this.updateColors()
      },
      onButtonColorMove(color) {
        this.button_color = color ? color.toHexString() : this.organization.button_color
        //console.log('onButtonColorMove', color, this.button_color)
      },
      onContentBackgroundColorChange(color) {
        this.content_background_color = color ? color.toHexString() : '#ffffff'
        //console.log('onContentBackgroundColorChange', color, this.content_background_color)
        this.updateColors()
      },
      onContentBackgroundColorMove(color) {
        this.content_background_color = color ? color.toHexString() : '#ffffff'
        //console.log('onContentBackgroundColorMove', color, this.content_background_color)
      },
      onClickResetDefault() {
        this.$bvModal.msgBoxConfirm(trans('template_default_reset_confirm'), {
          title: this.template.name,
          okTitle: trans('reset'),
          okVariant: 'danger',
          cancelTitle: trans('cancel')
        })
        .then(confirmed => {
          if (!confirmed) return
          const url = `/organization/${this.organization.id}/template/${this.template.id}/reset_default`
          this.busy = true
          this.error = null
          axios.post(url)
          .then(response => {
            window.location.reload()
          })
          .catch(error => {
            this.busy = false
            this.error = _.errorFromAjax(error)
            _.toastError(this, this.error)
          })
        })
      },
      updatePage() {
        const url = `/organization/${this.organization.id}/template/${this.template.id}/update_page`
        this.busy = true
        this.error = null
        axios.patch(url, {
          page_id: this.page_id,
        })
        .then(response => {
          this.busy = false
          this.page_id = response.data.page_id
          this.$emit('saved')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      updateColors() {
        const url = `/organization/${this.organization.id}/template/${this.template.id}/update_colors`
        this.busy = true
        this.error = null
        axios.patch(url, {
          button_color: this.button_color,
          content_background_color: this.content_background_color
        })
        .then(response => {
          this.busy = false
          this.button_color = response.data.button_color
          this.content_background_color = response.data.content_background_color
          this.$emit('saved')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onShowBrandHeaderChange() {
        let url = `/organization/${this.organization.id}/template/${this.template.id}/show_brand_header`
        axios.patch(url, {show_brand_header: this.show_brand_header, use_full_logo: this.use_full_logo})
        .then(response => {
          this.$emit('saved')
        })
        .catch(error => {
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onPublishToPartnerChange() {
        let url = `/organization/${this.organization.id}/template/${this.template.id}/published_to_partner`
        axios.patch(url, {published_to_partner: this.published_to_partner})
        .then(response => {
          this.$emit('saved')
        })
        .catch(error => {
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onOrderChange() {
        let url = `/organization/${this.organization.id}/template/${this.template.id}/update_blocks_order`
        let ids = _.map(this.blocks, block => {
          return block.id
        })
        axios.get(url, {params: {ids: ids}})
        .then(response => {
          this.$emit('saved')
        })
        .catch(error => {
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      onUseFullLogoChange() {
        if (this.show_brand_header && !!this.organization.full_logo) {
          this.onShowBrandHeaderChange()
        }
      },
      loadBlocks() {
        axios.get(this.ajaxUrl)
        .then(response => {
          this.blocks = response.data
        })
        .catch(error => {
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
      loadColors() {
        let url = `/organization/${this.organization.id}/template/colors`
        axios.get(url)
        .then(response => {
          $('#button_color').spectrum({
            preferredFormat: "hex",
            showInput: true,
            cancelText: trans('cancel'),
            chooseText: trans('choose'),
            allowEmpty: false,
            showInitial: true,
            clickoutFiresChange: true,
            showButtons: false,
            showPalette: true,
            palette: _.fixPalette(_.uniq(_.compact(_.union(this.organization.brand_colors, response.data.button_colors)))),
            hide: this.onButtonColorChange.bind(this),
            //change: this.onButtonColorChange.bind(this),
            move: this.onButtonColorMove.bind(this),
          })
          $('#content_background_color').spectrum({
            preferredFormat: "hex",
            showInput: true,
            cancelText: trans('cancel'),
            chooseText: trans('choose'),
            allowEmpty: false,
            showInitial: true,
            clickoutFiresChange: true,
            showButtons: false,
            showPalette: true,
            palette: _.fixPalette(response.data.content_background_colors),
            hide: this.onContentBackgroundColorChange.bind(this),
            //change: this.onContentBackgroundColorChange.bind(this),
            move: this.onContentBackgroundColorMove.bind(this),
          })
        })
        .catch(error => {
          this.error = _.errorFromAjax(error)
          _.toastError(this, this.error)
        })
      },
    },
    mounted() {
      window.templateComponent = this
      this.loadBlocks();
      this.loadColors();
    },
  }
</script>